import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import UIMainTitle from '@flatfy/supernova2/src/UIKit/MainTitle';
import FlatsFilter from '../FlatsFilter/FlatsFilter';
import FlatsCard from '../FlatsCard/FlatsCard';
import FlatsTitle from '../FlatsTitle/FlatsTitle';
import FlatsEmptyStates from '../FlatsEmptyStates/FlatsEmptyStates';
import { ONE_MILLION, ONE_THOUSAND } from '../../Funnel/FunnelPrice/rangeHelper';
import { FlatsFiltersContentContext } from '../../FlatsFilters/FlatsFiltersContext';
import { isCurrencyUsd, getCurrencyLabel } from '../../helpers/currency';

const FlatsMenu = ({ lastUpdate, menuContent }) => {
  const { currency } = useContext(FlatsFiltersContentContext);
  const [filtersWithDivider, setFiltersWithDivider] = useState(false);
  const menuRef = useRef(null);
  const headerRef = useRef(null);
  const isUsd = isCurrencyUsd(currency);
  const currencyLabel = getCurrencyLabel(currency);
  const intl = useIntl();

  const formatPrice = (price) => {
    if (price >= ONE_MILLION) {
      return `${(price / ONE_MILLION).toFixed(2)} млн`;
    }
    if (price >= ONE_THOUSAND) {
      return `${(price / ONE_THOUSAND).toFixed()} ${intl.formatMessage({ id: 'thousand' })}`;
    }
    return String(price);
  };

  const getFlatOrApartmentLabel = (withFlats, withApartments) => {
    if (!withFlats && !withApartments) return '';
    const flatsLabel = intl.formatMessage({ id: 'flats' });
    const apartmentsLabel = intl.formatMessage({ id: 'apartments' });

    if (withFlats && withApartments) {
      return [flatsLabel, intl.formatMessage({ id: 'and' }), apartmentsLabel].join(' ');
    }

    if (withFlats) {
      return flatsLabel;
    }

    return apartmentsLabel;
  };

  const groupTitle = ({ roomsCount, price, withFlats, withApartments }) => {
    const flatOrApartmentLabel = getFlatOrApartmentLabel(withFlats, withApartments);
    if (price) {
      return (
        <FormattedMessage
          id="menu.roomsCountPrice"
          values={{
            roomsCount,
            price: isUsd
              ? `${currencyLabel} ${formatPrice(price)}`
              : `${formatPrice(price)} ${currencyLabel}`,
            livingType: flatOrApartmentLabel,
          }}
        />
      );
    }

    return (
      <FormattedMessage
        id="menu.roomsCount"
        values={{ roomsCount, livingType: flatOrApartmentLabel }}
      />
    );
  };

  const handleMenuScroll = () => {
    const scrollTop = menuRef.current.scrollTop;
    const top = headerRef.current.offsetHeight + 24;
    if (scrollTop >= top && !filtersWithDivider) {
      setFiltersWithDivider(true);
    } else if (scrollTop < top && filtersWithDivider) {
      setFiltersWithDivider(false);
    }
  };

  return (
    <div className="FlatsMenu" ref={menuRef} onScroll={handleMenuScroll}>
      <div className="FlatsMenu-header" ref={headerRef}>
        <FlatsTitle lastUpdate={lastUpdate} />
      </div>
      <FlatsFilter
        withDivider={filtersWithDivider}
        ref={menuRef}
        requestUrl="/api/filter-config/flat"
      />
      <div className="FlatsMenu-content">
        {menuContent.flatsGroupedByRoomCount.map((group) => (
          <div className="Flats-grid" key={group.rooms_count}>
            <UIMainTitle
              title={{
                content: groupTitle({
                  roomsCount: group.rooms_count,
                  price: isUsd ? group.min_price_total_usd : group.min_price_total_uah,
                  withFlats: group.hasFlats,
                  withApartments: group.hasApartments,
                }),
              }}
            />
            <div className="UIGrid">
              {group.items.map((item) => (
                <div className="UIGrid-col-6 UIGrid-col-md UIGrid-col-sm" key={item.flatId}>
                  <FlatsCard {...item} />
                </div>
              ))}
            </div>
          </div>
        ))}
        {menuContent.flatsGroupedByRoomCount.length === 0 && <FlatsEmptyStates />}
      </div>
    </div>
  );
};

FlatsMenu.propTypes = {
  lastUpdate: PropTypes.string,
  menuContent: PropTypes.shape({
    flatsGroupedByRoomCount: PropTypes.array,
    flatsBaseUrl: PropTypes.string.isRequired,
  }).isRequired,
};

export default FlatsMenu;
